import { Link } from "react-router-dom";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";
import { ArrowRight, Award, Calendar, Eye, Users, Shield, CheckCircle, Mail, Phone, FileText, Scale } from "lucide-react";

const ASPETTI = [
  {
    icon: FileText,
    titolo: "Ruolo del CCNL",
    testo: [
      "La normativa attribuisce un ruolo decisivo ai sistemi di classificazione professionale contenuti nei CCNL, che devono essere fondati su criteri oggettivi e neutrali rispetto al genere. L'applicazione di un CCNL stipulato dalle organizzazioni sindacali comparativamente più rappresentative costituisce infatti una presunzione di conformità ai principi di parità retributiva.",
    ],
  },
  {
    icon: Eye,
    titolo: "Trasparenza prima della assunzione",
    testo: [],
    items: [
      "I candidati devono essere informati della retribuzione iniziale o della relativa fascia retributiva associata alla posizione offerta.",
      "Le offerte di lavoro devono essere formulate in modo neutro (senza riferimento al genere).",
      "Tutte le informazioni devono essere rese disponibili direttamente negli avvisi o nei bandi di selezione.",
    ],
  },
  {
    icon: Users,
    titolo: "Diritto all'informazione dei lavoratori",
    testo: [
      "I datori di lavoro sono tenuti a rendere accessibili ai lavoratori, già in fase di assunzione, i criteri utilizzati per la determinazione delle retribuzioni e per la progressione economica. Per le imprese che applicano un CCNL stipulato dalle organizzazioni comparativamente più rappresentative, l'obbligo può essere assolto mediante il rinvio ai criteri previsti dalla contrattazione collettiva.",
      "I lavoratori hanno diritto di ottenere informazioni scritte sui livelli retributivi medi, distinti per sesso, relativi alle categorie che svolgono lo stesso lavoro o un lavoro di pari valore.",
    ],
  },
  {
    icon: Scale,
    titolo: "Meccanismi correttivi",
    testo: [
      "La valutazione congiunta delle retribuzioni diviene obbligatoria quando emerge una differenza retributiva pari almeno al 5% all'interno di una categoria professionale e il datore di lavoro non sia in grado di giustificarla sulla base di criteri oggettivi e neutrali rispetto al genere. In tali casi dovranno essere individuate e adottate misure correttive in collaborazione con i rappresentanti dei lavoratori.",
    ],
  },
  {
    icon: Calendar,
    titolo: "Gradualità degli obblighi di comunicazione",
    testo: [
      "Gli obblighi sono graduati in ragione delle dimensioni aziendali.",
      "Per le imprese con almeno 250 dipendenti la prima comunicazione va effettuata entro il 7 giugno 2027 e successivamente con cadenza annuale; per quelle con un numero di dipendenti compreso tra 150 e 249 c'è una periodicità triennale, mentre per la fascia 100-149 dipendenti l'obbligo scatterà dal 2031.",
    ],
  },
];

const VANTAGGI = [
  { icon: Shield, testo: "Promuove concretamente equità e non discriminazione, creando un ambiente di lavoro inclusivo e rispettoso." },
  { icon: Award, testo: "Rafforza la credibilità e la responsabilità sociale dell'associazione, dimostrando un impegno autentico verso la sostenibilità e i valori etici." },
  { icon: CheckCircle, testo: "Anticipa le migliori pratiche europee, posizionando Confcommercio Roma come punto di riferimento per innovazione e attenzione ai diritti." },
];

export default function ParitaGenerePage() {
  return (
    <main id="main-content">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[320px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1920&q=60&auto=format" alt="Parita di genere" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Certificazioni per le imprese: dalla certificazione sulla parità di genere alla trasparenza salariale</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8 p-6 bg-[#EBF5FF] rounded-xl">
            <Award size={32} className="text-[var(--confcom-blue)] shrink-0" aria-hidden="true" />
            <p className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">Confcommercio Roma è da tempo in possesso della Certificazione UNI/PdR 125: una scelta consapevole e lungimirante</p>
          </div>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">Confcommercio Roma ha scelto da tempo di intraprendere il percorso verso la certificazione per la parità di genere come impegno concreto a favore dell'inclusione e delle pari opportunità, ed anche come scelta consapevole, non solo in considerazione degli obblighi comunitari.</p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">Con il <strong className="text-[var(--confcom-text-primary)]">Decreto Legislativo 7 maggio 2026, n. 96</strong>, pubblicato nella Gazzetta Ufficiale n. 125 del 1° giugno 2026, il legislatore italiano ha dato attuazione alla direttiva <strong className="text-[var(--confcom-text-primary)]">(UE) 2023/970</strong>, volta a rafforzare l'applicazione del principio della parità di retribuzione tra uomini e donne. Questo provvedimento si inserisce in un più ampio percorso europeo di promozione dell'uguaglianza sostanziale nel mercato del lavoro.</p>
        </div>
      </section>

      {/* Trasparenza */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-6">Il punto fondamentale: la trasparenza</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6">Già con la Direttiva 2006/54/CE l'Europa aveva posto al centro il principio della parità di trattamento, ma i monitoraggi condotti dall'UE avevano evidenziato diverse criticità:</p>
          <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                <CheckCircle size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong className="text-[var(--confcom-text-primary)]">Scarsa trasparenza</strong> nei sistemi retributivi</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                <CheckCircle size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong className="text-[var(--confcom-text-primary)]">Incertezza giuridica</strong> sul concetto di "lavoro di pari valore"</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                <CheckCircle size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                <strong className="text-[var(--confcom-text-primary)]">Ostacoli procedurali</strong>
              </li>
            </ul>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">Le nuove disposizioni, in vigore dal <strong className="text-[var(--confcom-text-primary)]">7 giugno 2026</strong>, si applicano a <strong className="text-[var(--confcom-text-primary)]">tutti i lavoratori subordinati</strong>, senza distinzioni: sia a tempo determinato sia a tempo indeterminato, inclusi i dirigenti. Restano esclusi il lavoro domestico e il lavoro intermittente.</p>
        </div>
      </section>

      {/* Aspetti salienti */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Aspetti salienti</h2>

          <div className="grid md:grid-cols-2 gap-5">
            {ASPETTI.map((a) => (
              <div key={a.titolo} className="bg-[#EBF5FF] rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <a.icon size={20} className="text-[var(--confcom-blue)] shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h3 className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{a.titolo}</h3>
                    {a.testo.map((p, pi) => (
                      <p key={pi} className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-2 last:mb-0">{p}</p>
                    ))}
                    {a.items && (
                      <ul className="space-y-2 mt-2">
                        {a.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-[var(--confcom-text-secondary)] leading-relaxed">
                            <CheckCircle size={14} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scelta strategica */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-6">Una scelta strategica</h2>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6">La certificazione sulla parità di genere è una scelta strategica e consapevole che valorizza le persone e il loro contributo:</p>
              <ul className="space-y-3 mb-6">
                {VANTAGGI.map((v) => (
                  <li key={v.testo} className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                    <CheckCircle size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{v.testo}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">La direzione è chiara: la <strong className="text-[var(--confcom-text-primary)]">trasparenza retributiva</strong> diventa il cuore della lotta alle discriminazioni.</p>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">Confcommercio Roma ha scelto di essere protagonista del cambiamento, a fianco delle imprese e dei lavoratori, per costruire un mercato del lavoro più giusto, inclusivo e competitivo.</p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80&auto=format" alt="Parità di genere" className="w-full h-80 lg:h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
        </div>
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per maggiori informazioni</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mt-3 mb-8">Contattaci</h2>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-left" data-testid="parita-genere-contatti">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <Mail size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="mailto:sindacale@confcommercioroma.it" className="hover:text-white transition-colors">sindacale@confcommercioroma.it</a>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Phone size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="tel:+390668437221" className="hover:text-white transition-colors">Tel: 06 68437 221-290</a>
              </div>
            </div>
          </div>

          <Link
            to="/chi-siamo/struttura-tecnica#ufficio-lavoro-e-welfare"
            data-testid="parita-genere-scopri-team"
            className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-md font-semibold hover:bg-white/90 transition-colors text-sm mt-8"
          >
            Scopri il Team <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
}
